/**
 * The DOM side of the connection dialog's transition (Option C, C1b, plan §3.2 "Measurement"): it reads the flip
 * layers of the dialog (`root`, the dialog content element) in one pass, and writes the planned animations. Reads never
 * interleave with writes: the First snapshot is read before React commits, the Last scene in one pass after, then every
 * `animate()` call is issued.
 *
 * Each animated layer is measured itself (never its wrapper), and `getBoundingClientRect` includes in-flight transforms,
 * so an interrupted flight reverses from where the layers visually are. A layer with no box (a ghost outside a flight is
 * `display: none`) has no First.
 */
import { frameScale, localBox, type Box, type RectLike } from './flipMath';
import { layerId, type AnimationSpec, type FlipScene, type LayerState, type RailSwitchScene } from './flipPlan';
import type { FlipController } from './flipController';
import { removeOutgoing } from './flipOutgoing';

export const FLIPPING_ATTRIBUTE = 'data-flipping';
/** Set for as long as any of the dialog's animations runs: no flip layer takes a pointer (review C1B-01). */
export const BUSY_ATTRIBUTE = 'data-flip-busy';

type Frame = { rect: RectLike; scale: number };

function frameOf(root: HTMLElement): Frame {
  const rect = root.getBoundingClientRect();
  return { rect, scale: frameScale(rect.width, root.offsetWidth) };
}

function boxOf(element: Element, frame: Frame): Box | null {
  const rect = element.getBoundingClientRect();
  if (rect.width === 0 && rect.height === 0) return null;
  return localBox(rect, frame.rect, frame.scale);
}

/**
 * The uniform scale a computed `transform` draws with (`none` → 1). The layers only translate and scale, so the first
 * column of the matrix is the scale. Read from the transform itself, never from `offsetHeight` (an integer: a 17.5-px
 * line box reads 18, review C1B-02).
 */
export function drawnScale(transform: string): number {
  const match = /^matrix(3d)?\(([^)]+)\)$/u.exec(transform.trim());
  if (!match) return 1;
  const [a, b] = match[2].split(',').map((part) => Number.parseFloat(part));
  const scale = Math.hypot(a, b);
  return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

function stateOf(element: HTMLElement, frame: Frame): LayerState | null {
  const box = boxOf(element, frame);
  if (!box) return null;
  const style = getComputedStyle(element);
  const opacity = Number.parseFloat(style.opacity);
  return {
    box,
    opacity: Number.isFinite(opacity) ? opacity : 1,
    // The DRAWN font size: a label caught mid-flight is scaled, and the reverse must start at the size it shows.
    fontSize: (Number.parseFloat(style.fontSize) || 0) * drawnScale(style.transform ?? ''),
    fontWeight: Number.parseFloat(style.fontWeight) || 400,
  };
}

type Layer = { element: HTMLElement; key: string; name: string };

function layersOf(root: HTMLElement): Layer[] {
  const layers: Layer[] = [];
  root.querySelectorAll<HTMLElement>('[data-flip-layer]').forEach((element) => {
    const key = element.closest<HTMLElement>('[data-flip-key]')?.dataset.flipKey;
    const name = element.dataset.flipLayer;
    if (key && name) layers.push({ element, key, name });
  });
  return layers;
}

/** The First boxes, read before the navigation commits. */
export function snapshotLayers(root: HTMLElement): Map<string, LayerState> {
  const frame = frameOf(root);
  const first = new Map<string, LayerState>();
  for (const { element, key, name } of layersOf(root)) {
    const state = stateOf(element, frame);
    if (state) first.set(layerId(key, name), state);
  }
  return first;
}

/** The Last scene, read in one pass after the commit (with `data-flipping` set, so the ghosts have boxes). */
export function readScene(root: HTMLElement, first: ReadonlyMap<string, LayerState>): FlipScene<HTMLElement> {
  const frame = frameOf(root);
  const layers: FlipScene<HTMLElement>['layers'][number][] = [];
  for (const { element, key, name } of layersOf(root)) {
    const state = stateOf(element, frame);
    if (state) layers.push({ target: element, key, name, ghost: element.hasAttribute('data-flip-ghost'), state });
  }
  const items = Array.from(root.querySelectorAll<HTMLElement>('[data-flip-key]'), (target) => ({ target, key: target.dataset.flipKey ?? '' }));
  const chrome = Array.from(root.querySelectorAll<HTMLElement>('[data-flip-chrome]'), (target) => ({
    target,
    kind: target.dataset.flipChrome === 'pane' ? ('pane' as const) : ('fade' as const),
  }));
  return { first, layers, items, chrome };
}

export function pagesOf(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('[data-flip-page]'));
}

const surfaceOf = (root: HTMLElement, key: string) => root.querySelector<HTMLElement>(`[data-flip-key="${key}"] [data-flip-layer="surface"]`);

/** The pane and, when both rail items are on screen, the highlight's move from the previous item to the chosen one. */
export function readRailSwitch(root: HTMLElement, fromKey: string, toKey: string): RailSwitchScene<HTMLElement> {
  const frame = frameOf(root);
  const pane = root.querySelector<HTMLElement>('[data-flip-chrome="pane"]');
  const previous = surfaceOf(root, fromKey);
  const chosen = surfaceOf(root, toKey);
  const from = previous ? boxOf(previous, frame) : null;
  const to = chosen ? boxOf(chosen, frame) : null;
  return { pane, highlight: chosen && from && to ? { target: chosen, key: toKey, from, to } : null };
}

/**
 * Issues the planned animations (the writes) and hands them to the controller. A spec marked `removeWhenDone` (an
 * outgoing copy) leaves the document as soon as its own fade ends.
 */
export function playSpecs(specs: readonly AnimationSpec<HTMLElement>[], controller: FlipController): void {
  for (const spec of specs) {
    if (spec.style) spec.target.style.width = spec.style.width;
    const { duration, delay = 0, easing, fill = 'none' } = spec.timing;
    const animation = spec.target.animate(spec.keyframes, { duration, delay, easing, fill });
    if (spec.removeWhenDone) animation.finished.then(() => spec.target.remove(), () => undefined);
    controller.track(animation, spec.meta);
  }
  controller.endIfIdle();
}

/**
 * The start of any animation run: whatever an interrupted run left is cleared (so the new view is measured at rest),
 * and the flip layers stop taking pointers until the run ends. A FLIP (`flipping`) also shows the ghosts, stacks the
 * rail above the pane, and opens every scroller whose content fits (`overflow: visible`) — layers flying in from outside
 * its box stay visible and cannot give it a scrollbar (a scrollbar would narrow the card grid mid-flight). A scroller
 * that really scrolls stays as it is.
 */
export function beginFlight(root: HTMLElement, { flipping }: { flipping: boolean }): void {
  clearFlight(root);
  root.setAttribute(BUSY_ATTRIBUTE, '');
  if (!flipping) return;
  root.querySelectorAll<HTMLElement>('[data-flip-scroller]').forEach((scroller) => {
    if (scroller.scrollHeight <= scroller.clientHeight + 1) scroller.setAttribute('data-flip-open', '');
  });
  root.setAttribute(FLIPPING_ATTRIBUTE, '');
}

/** The end of a run: no lift, no busy mark, no opened scroller, no outgoing copy, no ghosts, no inline widths. */
export function clearFlight(root: HTMLElement | null): void {
  if (!root) return;
  root.removeAttribute(FLIPPING_ATTRIBUTE);
  root.removeAttribute(BUSY_ATTRIBUTE);
  removeOutgoing(root);
  root.querySelectorAll('[data-flip-open]').forEach((scroller) => scroller.removeAttribute('data-flip-open'));
  root.querySelectorAll<HTMLElement>('[data-flip-ghost]').forEach((ghost) => ghost.style.removeProperty('width'));
}
